import mustache from "mustache";
import { CompraAnimalesResponse } from "../../../../../modulos/pesaje/ganaderia/response/CompraAnimalesResponse";
import { daysFrom, daysUntil, formatFechaCorta } from "../../../../../utils/fechaUtils";
import { _miles } from "../../../../../utils/numberUtils";
import { IConnectRender } from "../../../../definitions/IConnectRender";
import template from "./compraListItemTemplate.html";

mustache.parse(template);

/** Contador para ids únicos por instancia. */
let COMPRA_ITEM_ID = 0;

/** Acciones de una fila de compra. */
export type CompraListItemActions = {
  /** Ver el detalle de la compra. */
  onView: (compra: CompraAnimalesResponse) => void;
  /** Editar la compra. */
  onEdit: (compra: CompraAnimalesResponse) => void;
  /** Eliminar la compra. */
  onDelete: (compra: CompraAnimalesResponse) => void;
};

/** Fila de la lista de compras (Render/Connect): avatar (código), lote actual,
 *  animales disponibles y acciones ver/editar/eliminar. */
export class CompraListItemRender implements IConnectRender {
  /** Id único de la instancia (para los ids del DOM). */
  private readonly _itemId: number;

  /**
   * @param _compra  Compra a mostrar.
   * @param _actions Handlers de ver/editar/eliminar.
   */
  constructor(
    private readonly _compra: CompraAnimalesResponse,
    private readonly _actions: CompraListItemActions,
  ) {
    COMPRA_ITEM_ID += 1;
    this._itemId = COMPRA_ITEM_ID;
  }

  /** HTML de la fila. */
  render(): string {
    const c = this._compra;
    // Peso ganado = días en finca × ganancia diaria (gdp en g/día → kg) × animales.
    const pesoGanado =
      daysFrom(c.fechaCompra) *
      (c.gdpEsperadoGr / 1000) *
      c.cantidadAnimalesCompra;

    // Fecha esperada de salida: la calcula el server. Variante según días restantes.
    let fechaSalida = "—";
    let fechaSalidaVariant = "neutral";
    if (c.fechaEsperadaSalida) {
      fechaSalida = formatFechaCorta(c.fechaEsperadaSalida);
      const restantes = daysUntil(c.fechaEsperadaSalida);
      if (restantes > 15) fechaSalidaVariant = "success";
      else if (restantes > 1) fechaSalidaVariant = "warning";
      else fechaSalidaVariant = "error";
    }

    return mustache.render(template, {
      id: this._itemId,
      codigo: c.codigo,
      lote: c.loteActual.nombreLote,
      secondary: [
        formatFechaCorta(c.fechaCompra),
        `${c.animalesDisponibles} / ${c.cantidadAnimalesCompra} animales`,
      ].join(" · "),
      pesoGanado: _miles(pesoGanado),
      fechaSalida: fechaSalida,
      fechaSalidaVariant: fechaSalidaVariant,
    });
  }

  /** Cablea los 3 botones de acción. */
  connect(): void {
    this._view = document.getElementById(`compra-view-${this._itemId}`);
    this._edit = document.getElementById(`compra-edit-${this._itemId}`);
    this._delete = document.getElementById(`compra-delete-${this._itemId}`);
    this._view?.addEventListener("click", this._onView);
    this._edit?.addEventListener("click", this._onEdit);
    this._delete?.addEventListener("click", this._onDelete);
  }

  /** Libera los listeners. */
  dispose(): void {
    this._view?.removeEventListener("click", this._onView);
    this._edit?.removeEventListener("click", this._onEdit);
    this._delete?.removeEventListener("click", this._onDelete);
  }

  private _view: HTMLElement | null = null;
  private _edit: HTMLElement | null = null;
  private _delete: HTMLElement | null = null;

  private _onView = (): void => this._actions.onView(this._compra);
  private _onEdit = (): void => this._actions.onEdit(this._compra);
  private _onDelete = (): void => this._actions.onDelete(this._compra);
}
